/**
 * Chat Hooks - React hooks for internal chat
 */

import {
  getChatUsers,
  getConversationMessages,
  getConversations,
  mapApiMessageToChatMessage,
  mapConversationToChat,
  markConversationAsRead,
  sendMessage,
  startConversation,
  type ApiChatUser,
} from '@/api/chat';
import type { Chat, ChatMessage } from '@/pages/chat/types';
import { useAuthStore } from '@/stores/auth-store';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';

const CONVERSATIONS_POLL_MS = 15_000; // 15 s
const MESSAGES_POLL_MS = 5_000; // 5 s

// ============================================
// HOOKS
// ============================================

interface UseConversationsResult {
  chats: Chat[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useConversations(): UseConversationsResult {
  const { t } = useTranslation();
  const [chats, setChats] = useState<Chat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initialLoadDone = useRef(false);

  const fetchConversations = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        const conversations = await getConversations();
        setChats(conversations.map(c => mapConversationToChat(c)));
        initialLoadDone.current = true;
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.chat.loadConversations');
        if (!silent) setError(message);
        console.error('Error fetching conversations:', err);
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    void fetchConversations();
    const interval = setInterval(() => {
      void fetchConversations(true);
    }, CONVERSATIONS_POLL_MS);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  return { chats, isLoading, error, refetch: () => fetchConversations() };
}

interface UseConversationMessagesResult {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useConversationMessages(
  conversationId: string | null
): UseConversationMessagesResult {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentUserId = useAuthStore(s => s.user?.id ?? '');

  const fetchMessages = useCallback(
    async (silent = false) => {
      if (!conversationId) {
        setMessages([]);
        return;
      }
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        const apiMessages = await getConversationMessages(conversationId);
        setMessages(apiMessages.map(m => mapApiMessageToChatMessage(m, currentUserId)));
        await markConversationAsRead(conversationId).catch(() => {
          // Non-critical, ignore error
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.chat.loadMessages');
        if (!silent) setError(message);
        console.error('Error fetching messages:', err);
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [conversationId, currentUserId, t]
  );

  useEffect(() => {
    void fetchMessages();
    const interval = setInterval(() => {
      void fetchMessages(true);
    }, MESSAGES_POLL_MS);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  return { messages, isLoading, error, refetch: () => fetchMessages() };
}

interface UseSendMessageResult {
  send: (conversationId: string, content: string) => Promise<ChatMessage | null>;
  isLoading: boolean;
  error: string | null;
}

export function useSendMessage(): UseSendMessageResult {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentUserId = useAuthStore(s => s.user?.id ?? '');

  const send = useCallback(
    async (conversationId: string, content: string): Promise<ChatMessage | null> => {
      setIsLoading(true);
      setError(null);
      try {
        const apiMsg = await sendMessage(conversationId, content);
        return mapApiMessageToChatMessage(apiMsg, currentUserId);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.chat.sendMessage');
        setError(message);
        console.error('Error sending message:', err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [currentUserId, t]
  );

  return { send, isLoading, error };
}

interface UseStartConversationResult {
  start: (
    participantIds: string[],
    options?: { initialMessage?: string; name?: string }
  ) => Promise<Chat | null>;
  isLoading: boolean;
  error: string | null;
}

export function useStartConversation(): UseStartConversationResult {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(
    async (
      participantIds: string[],
      options?: { initialMessage?: string; name?: string }
    ): Promise<Chat | null> => {
      setIsLoading(true);
      setError(null);
      try {
        const conv = await startConversation(participantIds, options);
        return mapConversationToChat(conv);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.chat.createConversation');
        setError(message);
        console.error('Error starting conversation:', err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  return { start, isLoading, error };
}

interface UseChatUsersResult {
  users: ApiChatUser[];
  isLoading: boolean;
  error: string | null;
}

export function useChatUsers(): UseChatUsersResult {
  const { t } = useTranslation();
  const [users, setUsers] = useState<ApiChatUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setUsers(await getChatUsers());
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.chat.loadUsers');
      setError(message);
      console.error('Error fetching chat users:', err);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  return { users, isLoading, error };
}
